import React, { useState, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { view, i18next, readOnly } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import showReadOnlyModeDialog from 'src/js/modules/read-only-mode/helpers/show-read-only-mode-dialog';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import elementSettings from 'src/js/modules/element-settings';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import mediaTypeIds from '../constants/media-type-ids';
import getMediaTypeId from '../helpers/get-media-type-id';
import {
  getImageFieldsValues,
  getIframeFieldsValues,
  getLinkFieldsValues,
} from '../helpers/get-fields-values';
import mediaContainerBuilder from '../helpers/media-container-builder';
import trackMediaTypeChanged from '../trackers/track-media-type-changed';
import useDataCache from '../hooks/use-data-cache';
import ImageSettingsContainer from './image-settings-container';
import IframeSettingsContainer from './iframe-settings-container';

const { Box, TabSelector } = designSystem;
const { Context: ElementSettingsContext } = elementSettings;

function MediaSwitcherContainer({ tabId, elementId, settingId }) {
  const {
    form: { getValues },
    analyticsTraits: { blockId, blockVariationId },
  } = useContext(ElementSettingsContext);

  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;

  const mediaContainerElement = view.accessors.getLiveElement(elementId);
  const initialMediaTypeId = getMediaTypeId(mediaContainerElement);

  const [currentMediaTypeId, setCurrentMediaTypeId] = useState(initialMediaTypeId);
  const imageCachedFieldsValues = useDataCache();
  const iframeCachedFieldsValues = useDataCache();

  const cachedFieldsValuesMap = useMemo(
    () => ({
      [mediaTypeIds.IMAGE]: imageCachedFieldsValues,
      [mediaTypeIds.VIDEO]: iframeCachedFieldsValues,
    }),
    [imageCachedFieldsValues, iframeCachedFieldsValues]
  );

  const cacheFieldsValues = useCallback(
    (mediaTypeId) => {
      switch (mediaTypeId) {
        case mediaTypeIds.IMAGE: {
          const imageFieldsValues = getImageFieldsValues(getValues);
          const linkFieldsValues = getLinkFieldsValues(getValues);

          imageCachedFieldsValues.save({
            ...imageFieldsValues,
            ...linkFieldsValues,
          });
          break;
        }
        case mediaTypeIds.VIDEO: {
          const iframeFieldsValues = getIframeFieldsValues(getValues);

          iframeCachedFieldsValues.save(iframeFieldsValues);
          break;
        }
        default: {
          break;
        }
      }
    },
    [imageCachedFieldsValues, iframeCachedFieldsValues, getValues]
  );

  const handleRebuildMediaContainer = useCallback(
    (selectedMediaTypeId) => {
      const cachedFieldsValues = cachedFieldsValuesMap[selectedMediaTypeId].get();

      mediaContainerBuilder.replaceContentFromFieldsValues(
        mediaContainerElement,
        selectedMediaTypeId,
        cachedFieldsValues,
        activeBreakpoint
      );
    },
    [cachedFieldsValuesMap, mediaContainerElement, activeBreakpoint]
  );

  const handleTypeChange = useCallback(
    (selectedMediaTypeId) => {
      if (readOnly.verifiers.isReadOnlyModeEnabled()) {
        showReadOnlyModeDialog();
        return;
      }

      if (selectedMediaTypeId === currentMediaTypeId) {
        return;
      }

      trackMediaTypeChanged({
        tabId,
        oldMediaType: currentMediaTypeId,
        newMediaType: selectedMediaTypeId,
        blockId,
        blockVariationId,
      });

      cacheFieldsValues(currentMediaTypeId);
      handleRebuildMediaContainer(selectedMediaTypeId);
      setCurrentMediaTypeId(selectedMediaTypeId);
    },
    [
      currentMediaTypeId,
      cacheFieldsValues,
      handleRebuildMediaContainer,
      tabId,
      blockId,
      blockVariationId,
    ]
  );

  return (
    <Box paddingTop="spacing-xs" paddingBottom="spacing-xs">
      <Box paddingLeft="spacing-m" paddingRight="spacing-m" paddingBottom="spacing-xs">
        <TabSelector
          activeTabId={currentMediaTypeId}
          tabs={[
            {
              id: mediaTypeIds.IMAGE,
              label: i18next.t('Image'),
            },
            {
              id: mediaTypeIds.VIDEO,
              label: i18next.t('Video'),
            },
          ]}
          onClick={handleTypeChange}
        />
      </Box>
      {currentMediaTypeId === mediaTypeIds.IMAGE && (
        <ImageSettingsContainer
          settingId={settingId}
          tabId={tabId}
          mediaContainerElement={mediaContainerElement}
          cachedFieldsValues={imageCachedFieldsValues.get()}
        />
      )}
      {currentMediaTypeId === mediaTypeIds.VIDEO && (
        <IframeSettingsContainer
          mediaContainerElement={mediaContainerElement}
          settingId={settingId}
          tabId={tabId}
          cachedFieldsValues={iframeCachedFieldsValues.get()}
        />
      )}
    </Box>
  );
}

MediaSwitcherContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default MediaSwitcherContainer;
